import { destinationPagePublicPath } from '@utazzo/shared';
import { Pencil } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDeleteDestinationPage, useDestinationPagesList } from '../use-destination-pages';

import type { DataGridColumn, DataGridFilter } from '@/components/data-grid/types';
import type { ListScreenColumns } from '@/components/list-screen/types';
import type {
  WebsiteDestinationPageListItem,
  WebsiteDestinationPageListQuery,
  WebsitePublishStatus,
} from '@utazzo/shared';

import { hiddenFilterId } from '@/components/data-grid/types';
import { ListScreen } from '@/components/list-screen/ListScreen';
import { ViewOnSiteLink } from '@/components/view-on-site-link';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useDestinationsList } from '@/features/destinations/use-destinations';
import { WEBSITE_PUBLISH_STATUS_LABEL, humanEnum } from '@/lib/enum-labels';
import { destinationPublicUrl } from '@/lib/website-urls';

const STATUS_TONE: Record<WebsitePublishStatus, string> = {
  DRAFT: 'bg-zinc-100 text-zinc-600',
  PENDING_REVIEW: 'bg-amber-50 text-amber-700',
  PUBLISHED: 'bg-emerald-50 text-emerald-700',
  ARCHIVED: 'bg-rose-50 text-rose-700',
};

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_REVIEW', label: 'In review' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

/**
 * Deep-link target for the dashboard's Needs-attention chips. The server
 * applies the same predicate the dashboard counted with, so a chip reading
 * "102 destination pages" opens a list of exactly 102.
 */
const COMPLETENESS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'INCOMPLETE', label: 'Missing info (any)' },
  { value: 'SEO', label: 'Missing SEO' },
  { value: 'CONTENT', label: 'Missing content' },
];

const FILTERS: ReadonlyArray<DataGridFilter> = [
  { id: 'status', label: 'Status', options: STATUS_OPTIONS, widthClass: 'w-48' },
  {
    id: 'completeness',
    label: 'Completeness',
    options: COMPLETENESS_OPTIONS,
    widthClass: 'w-44',
  },
  // Deep-link targets for the website dashboard's breakdown tables. Hidden: no
  // chip, just a removable pill while one is active.
  { id: 'destination', label: 'Destination', options: [], hidden: true },
  {
    id: 'scope',
    label: 'Destination type',
    options: [
      { value: 'international', label: 'International' },
      { value: 'domestic', label: 'Domestic' },
      { value: 'none', label: 'Not linked to a destination' },
    ],
    hidden: true,
  },
];

export const DestinationPagesListPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deletePage = useDeleteDestinationPage();
  const destinationsQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
  });
  const destName = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of destinationsQuery.data?.data ?? []) m.set(d.id, d.name);
    return m;
  }, [destinationsQuery.data]);

  const columns: ListScreenColumns<WebsiteDestinationPageListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<WebsiteDestinationPageListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'title',
        header: 'Destination page',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.title}</span>
            {row.primaryCategorySlug ? (
              <ViewOnSiteLink
                href={destinationPublicUrl(row.slug, row.primaryCategorySlug)}
                label={destinationPagePublicPath(row.primaryCategorySlug, row.slug)}
              />
            ) : (
              <span className="text-xs text-zinc-500">/{row.slug} (assign a category)</span>
            )}
          </div>
        ),
      },
      {
        id: 'destination',
        header: 'Destination',
        cell: (row) => (
          <span className="text-sm text-zinc-700">
            {row.destinationId ? (destName.get(row.destinationId) ?? '-') : '-'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <span
            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}
          >
            {humanEnum(WEBSITE_PUBLISH_STATUS_LABEL, row.status)}
          </span>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<WebsiteDestinationPageListItem, WebsiteDestinationPageListQuery>
      title="Destination pages"
      description="Public marketing pages per destination - hero, SEO, collection sliders, FAQ, reels, reviews."
      useList={useDestinationPagesList}
      defaultSort={{ by: 'updatedAt', dir: 'desc' }}
      minSearchLength={3}
      toQuery={(state, base) => ({
        ...base,
        ...(state.filters.status ? { status: state.filters.status as WebsitePublishStatus } : {}),
        ...(state.filters.completeness === 'INCOMPLETE' ? { incomplete: true } : {}),
        ...(state.filters.completeness === 'SEO' || state.filters.completeness === 'CONTENT'
          ? { missing: state.filters.completeness as WebsiteDestinationPageListQuery['missing'] }
          : {}),
        ...(state.filters.destination
          ? {
              destinationId: hiddenFilterId(
                state.filters.destination,
              ) as WebsiteDestinationPageListQuery['destinationId'],
            }
          : {}),
        ...(state.filters.scope
          ? {
              destinationScope: state.filters
                .scope as WebsiteDestinationPageListQuery['destinationScope'],
            }
          : {}),
      })}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`/destination-pages/${row.id}`)}
      createButton={{
        label: 'Add destination page',
        onClick: () => navigate('/destination-pages/new'),
      }}
      rowActions={{
        extra: (row) => [
          {
            type: 'item',
            label: 'Edit',
            icon: <Pencil className="h-4 w-4" />,
            onSelect: () => navigate(`/destination-pages/${row.id}`),
          },
        ],
        delete: {
          onDelete: (row) => deletePage.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this destination page?',
            description: `${row.title} will be soft-deleted.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by title or slug (min 3 characters)',
        emptyTitle: 'No destination pages yet',
        emptyDescription: 'Create one to build a public destination landing page.',
        errorTitle: 'Failed to load destination pages',
        errorDescription: 'Please retry.',
      }}
    />
  );
};
